import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
} from '@material-ui/core';
import BallotIcon from '@material-ui/icons/Ballot';
import RouterIcon from '@material-ui/icons/Router';
import WrapTextIcon from '@material-ui/icons/WrapText';
import FindReplaceIcon from '@material-ui/icons/FindReplace';
import RouterLogSummary from '../cards/RouterLogSummary';
import PolicyRouterRequestCard from '../cards/PolicyRouterRequestCard';
import PolicyRouterResponseCard from '../cards/PolicyRouterResponseCard';
import WrapperApiRequestCard from '../cards/WrapperApiRequestCard';
import WrapperApiResponseCard from '../cards/WrapperApiResponseCard';
import WrapperApiSourceRequestCard from '../cards/WrapperApiSourceRequestCard';
import WrapperApiSourceResponseCard from '../cards/WrapperApiSourceResponseCard';
import { POLICY_ROUTER_ECO_SERVICES } from '../constants/policyRouterServices';
import {
  getRouterRequest,
  getRouterResponse,
  getWrapperRequest,
  getWrapperResponse,
  getSourceRequests,
  getSourceResponses,
  hasPolicyLookupData,
} from '../utils/logExtractors';

const useStyles = makeStyles((theme) => ({
  headerBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  divider: {
    marginBottom: theme.spacing(2),
  },
  sourceLinkContainer: {
    marginBottom: theme.spacing(2),
    textAlign: 'right',
  },
  sourceDetailsLink: {
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const LogDetailsModal = ({
  open,
  selectedLog,
  activeService,
  setActiveService,
  onClose,
  classes,
}) => {
  const styles = useStyles();
  const [showSourceDetails, setShowSourceDetails] = useState(false);
  const hasWrapperData = !!(getWrapperRequest(selectedLog) || getWrapperResponse(selectedLog));

  // Reset source details state when modal closes
  const handleClose = () => {
    setShowSourceDetails(false);
    onClose();
  };

  // Reset source details when service changes
  const handleServiceChange = (service) => {
    setShowSourceDetails(false);
    setActiveService(service);
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      className={classes.logDetailsDialog}
    >
      <DialogTitle className={classes.logSearchDialogHeader}>
        <Box className={styles.headerBox}>
          <BallotIcon />
          <Typography
            variant="h5"
            className={classes.logSearchDialogHeaderTypography}
          >
            Transaction Details
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.container}>
          
          {/* Service Sidebar */}
          <div className={classes.sidebar}>
            <List component="nav">
              <ListItem
                button
                selected={activeService === POLICY_ROUTER_ECO_SERVICES.POLICY_ROUTER}
                onClick={() => handleServiceChange(POLICY_ROUTER_ECO_SERVICES.POLICY_ROUTER)}
              >
                <ListItemIcon>
                  <RouterIcon />
                </ListItemIcon>
                <ListItemText primary="Policy Router API" />
              </ListItem>

              <ListItem
                button
                selected={activeService === POLICY_ROUTER_ECO_SERVICES.WRAPPER}
                onClick={() => handleServiceChange(POLICY_ROUTER_ECO_SERVICES.WRAPPER)}
              >
                <ListItemIcon>
                  <WrapTextIcon />
                </ListItemIcon>
                <ListItemText primary="Wrapper API" />
              </ListItem>
              {selectedLog && hasPolicyLookupData(selectedLog) && (
                <ListItem
                  button
                  selected={activeService === POLICY_ROUTER_ECO_SERVICES.POLICY_LOOKUP}
                  onClick={() => handleServiceChange(POLICY_ROUTER_ECO_SERVICES.POLICY_LOOKUP)}
                >
                  <ListItemIcon>
                    <FindReplaceIcon />
                  </ListItemIcon>
                  <ListItemText primary="Policy Lookup API" />
                </ListItem>
              )}
            </List>
          </div>

          {/* Right Content Area */}
          <div className={classes.contentArea}>
            <RouterLogSummary 
              selectedLog={selectedLog}
              classes={classes}
            />

            <Divider className={styles.divider} />

            {/* Policy Router API Details */}
            {selectedLog && activeService === POLICY_ROUTER_ECO_SERVICES.POLICY_ROUTER && ( 
              <>
                <PolicyRouterRequestCard 
                  routerRequest={getRouterRequest(selectedLog)}
                  classes={classes}
                />
                <PolicyRouterResponseCard 
                  routerResponse={getRouterResponse(selectedLog)}
                  classes={classes}
                />
              </>
            )}
            
            {/* Wrapper API Details */}
            {selectedLog && activeService === POLICY_ROUTER_ECO_SERVICES.WRAPPER && (
              <>
                <WrapperApiRequestCard 
                  wrapperRequest={getWrapperRequest(selectedLog)}
                  classes={classes}
                />
                <WrapperApiResponseCard 
                  wrapperResponse={getWrapperResponse(selectedLog)}
                  classes={classes}
                />
                <Divider className={styles.divider} />
                <Box className={styles.sourceLinkContainer}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => setShowSourceDetails(prev => !prev)}
                    className={styles.sourceDetailsLink}
                  >
                    {hasWrapperData && (showSourceDetails ? "Hide Details" : "Show More..")}
                  </Link>
                </Box>
                {showSourceDetails && (
                  <>
                    <WrapperApiSourceRequestCard 
                      sourceRequest={getSourceRequests(selectedLog)}
                      classes={classes}
                    />
                    <WrapperApiSourceResponseCard 
                      sourceResponse={getSourceResponses(selectedLog)}
                      classes={classes}
                    />
                  </>
                )}
              </>
            )}
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
};

LogDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  selectedLog: PropTypes.object,
  activeService: PropTypes.string.isRequired,
  setActiveService: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

export default LogDetailsModal;
